<template>
  <div v-if="showTutorial" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
    <div class="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden">
      <!-- Header -->
      <div class="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 text-white">
        <div class="flex justify-between items-center">
          <h2 class="text-2xl font-bold">¡Bienvenido a Excurix! 🎉</h2>
          <button @click="skipTutorial" class="text-white hover:text-gray-200 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="mt-2 flex space-x-2">
          <div v-for="i in totalSteps" :key="i" 
               :class="['h-2 flex-1 rounded-full transition-all', i <= currentStep ? 'bg-white' : 'bg-white/30']">
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="p-8">
        <!-- Paso 1: Crear una reserva -->
        <div v-if="currentStep === 1" class="space-y-6">
          <div class="text-center">
            <div class="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4">
              <svg class="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 mb-2">Paso 1: Crear una Reserva</h3>
            <p class="text-gray-600 mb-6">Las reservas te permiten gestionar citas y servicios de tus clientes</p>
          </div>
          
          <div class="bg-blue-50 rounded-lg p-6 space-y-4">
            <div class="flex items-start space-x-3">
              <span class="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              <div>
                <p class="text-gray-700 font-medium">Ve al menú lateral y haz clic en "Reservas"</p>
              </div>
            </div>
            <div class="flex items-start space-x-3">
              <span class="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
              <div>
                <p class="text-gray-700 font-medium">Haz clic en el botón "+ Agregar" en la esquina superior derecha</p>
              </div>
            </div>
            <div class="flex items-start space-x-3">
              <span class="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
              <div>
                <p class="text-gray-700 font-medium">Completa los datos: cliente, servicio, fecha y hora</p>
              </div>
            </div>
          </div>

          <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm text-yellow-700">
                  <strong>Tip:</strong> Si el cliente no existe, puedes crearlo desde el formulario de reserva.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Paso 2: Gestionar Servicios -->
        <div v-if="currentStep === 2" class="space-y-6">
          <div class="text-center">
            <div class="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
              <svg class="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 mb-2">Paso 2: Gestionar Servicios</h3>
            <p class="text-gray-600 mb-6">Define los servicios que ofreces con sus precios y duración</p>
          </div>
          
          <div class="bg-green-50 rounded-lg p-6 space-y-4">
            <div class="flex items-start space-x-3">
              <span class="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              <div>
                <p class="text-gray-700 font-medium">Ve a "Servicios" en el menú lateral</p>
              </div>
            </div>
            <div class="flex items-start space-x-3">
              <span class="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
              <div>
                <p class="text-gray-700 font-medium">Agrega tus servicios con nombre, descripción y precio</p>
              </div>
            </div>
            <div class="flex items-start space-x-3">
              <span class="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
              <div>
                <p class="text-gray-700 font-medium">Configura la duración estimada de cada servicio</p>
              </div>
            </div>
          </div>

          <div class="bg-blue-50 border-l-4 border-blue-400 p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm text-blue-700">
                  <strong>Tip:</strong> Organiza tus servicios por categorías para encontrarlos más rápido.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Paso 3: Ver el Dashboard -->
        <div v-if="currentStep === 3" class="space-y-6">
          <div class="text-center">
            <div class="inline-flex items-center justify-center w-20 h-20 bg-purple-100 rounded-full mb-4">
              <svg class="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 mb-2">Paso 3: Dashboard y Reportes</h3>
            <p class="text-gray-600 mb-6">Visualiza el rendimiento de tu negocio en tiempo real</p>
          </div>
          
          <div class="bg-purple-50 rounded-lg p-6 space-y-4">
            <div class="flex items-start space-x-3">
              <span class="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              <div>
                <p class="text-gray-700 font-medium">Haz clic en "Dashboard" para ver estadísticas</p>
              </div>
            </div>
            <div class="flex items-start space-x-3">
              <span class="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
              <div>
                <p class="text-gray-700 font-medium">Revisa ingresos, servicios más vendidos y tendencias</p>
              </div>
            </div>
            <div class="flex items-start space-x-3">
              <span class="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
              <div>
                <p class="text-gray-700 font-medium">Filtra por fechas para análisis específicos</p>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 text-center">
              <svg class="w-8 h-8 text-blue-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
              <p class="text-sm font-semibold text-gray-700">Gestión de Clientes</p>
              <p class="text-xs text-gray-600 mt-1">Base de datos completa</p>
            </div>
            <div class="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 text-center">
              <svg class="w-8 h-8 text-green-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <p class="text-sm font-semibold text-gray-700">Control de Caja</p>
              <p class="text-xs text-gray-600 mt-1">Ingresos y egresos</p>
            </div>
          </div>
        </div>

        <!-- Paso final -->
        <div v-if="currentStep === 4" class="space-y-6">
          <div class="text-center">
            <div class="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mb-4">
              <svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 mb-2">¡Todo Listo! 🎊</h3>
            <p class="text-gray-600 mb-6">Ahora estás preparado para usar Excurix al máximo</p>
          </div>
          
          <div class="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
            <h4 class="font-bold text-gray-900 mb-4">Recuerda que también puedes:</h4>
            <ul class="space-y-3">
              <li class="flex items-center space-x-3">
                <svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                <span class="text-gray-700">Gestionar tu perfil y configuración de empresa</span>
              </li>
              <li class="flex items-center space-x-3">
                <svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                <span class="text-gray-700">Crear y gestionar presupuestos para tus clientes</span>
              </li>
              <li class="flex items-center space-x-3">
                <svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                <span class="text-gray-700">Administrar productos e inventario</span>
              </li>
              <li class="flex items-center space-x-3">
                <svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                <span class="text-gray-700">Agregar usuarios y definir permisos</span>
              </li>
            </ul>
          </div>

          <div class="bg-blue-600 text-white rounded-lg p-6 text-center">
            <p class="text-sm mb-2">¿Necesitas ayuda?</p>
            <p class="font-semibold">Contacta con nuestro soporte en cualquier momento</p>
          </div>
        </div>
      </div>

      <!-- Footer con botones -->
      <div class="bg-gray-50 px-8 py-4 flex justify-between items-center">
        <button v-if="currentStep > 1" 
                @click="previousStep" 
                class="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors font-medium">
          Anterior
        </button>
        <div v-else></div>
        
        <div class="flex space-x-3">
          <button v-if="currentStep < totalSteps" 
                  @click="skipTutorial" 
                  class="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors">
            Omitir
          </button>
          <button @click="nextStep" 
                  class="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-colors font-semibold shadow-lg">
            {{ currentStep === totalSteps ? '¡Empezar!' : 'Siguiente' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';

const showTutorial = ref(false);
const currentStep = ref(1);
const totalSteps = ref(4);

onMounted(() => {
  // Verificar si el usuario ya vio el tutorial
  const tutorialCompleted = localStorage.getItem('tutorialCompleted');
  const isFirstLogin = localStorage.getItem('isFirstLogin');
  
  if (!tutorialCompleted && isFirstLogin === 'true') {
    showTutorial.value = true;
  }
});

const nextStep = () => {
  if (currentStep.value < totalSteps.value) {
    currentStep.value++;
  } else {
    completeTutorial();
  }
};

const previousStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
  }
};

const skipTutorial = () => {
  localStorage.setItem('tutorialCompleted', 'true');
  localStorage.removeItem('isFirstLogin');
  showTutorial.value = false;
};

const completeTutorial = () => {
  localStorage.setItem('tutorialCompleted', 'true');
  localStorage.removeItem('isFirstLogin');
  showTutorial.value = false;
};

// Método para mostrar el tutorial manualmente (desde ayuda/configuración)
const showTutorialManually = () => {
  currentStep.value = 1;
  showTutorial.value = true;
};

defineExpose({
  showTutorialManually
});
</script>

<style scoped>
/* Animaciones suaves */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
